import React, { useCallback, useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { ArrowBack, DeleteOutline, EditOutlined } from "@mui/icons-material";
import {
    fetchQuestion,
    fetchAnswerSet,
    createQuestion,
    updateQuestion,
    deleteQuestion,
    fetchAnswerSets,
    fetchQuestionsPaginated,
} from "../../../utils/api";
import { InclusionMode, type Question } from "../../../types/rubric";
import type { AnswerSet, AnswerValue } from "../../../types/answerSet";
import theme from "../../../theme/Theme";
import RubricManagementList, { Column } from "../../molecules/RubricManagementList";
import { useListState } from "../../../hooks/useListState";

type ViewMode = "list" | "detail";

interface QuestionFormState {
    name: string;
    questionText: string;
    questionType: Question["questionType"];
    required: boolean;
    answerSetId: number | null;
    displayOrder?: number | null;
    active: boolean;
    selectedAnswerIds: number[];
    inclusionMode: InclusionMode;
}

interface QuestionsPanelProps {
    onPathChange?: (path: string) => void;
    onRequestNavigate?: (tab: "answerSets") => void;
}

const questionTypeOptions: { value: Question["questionType"]; label: string }[] = [
    { value: "MULTIPLE_CHOICE", label: "Multiple Choice" },
];

const initialFormState: QuestionFormState = {
    name: "",
    questionText: "",
    questionType: "MULTIPLE_CHOICE",
    required: true,
    answerSetId: null,
    displayOrder: undefined,
    active: true,
    selectedAnswerIds: [],
    inclusionMode: InclusionMode.EXCLUDE,
};

// Column configuration for the questions list
const questionColumns: Column<Question>[] = [
    {
        field: "name",
        label: "Name",
        sortable: true,
        render: (item) => (
            <Box>
                <Typography variant="body1" noWrap>
                    {item.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                    {item.questionText}
                </Typography>
                {item.answerSetName && (
                    <Typography variant="body2" color="text.secondary" noWrap>
                        {item.answers?.map((answer) => answer.name).join(", ")}
                    </Typography>
                )}
            </Box>
        ),
    },
    {
        field: "questionType",
        label: "Type",
        sortable: false,
        width: 200,
        render: (item) => (
            <Stack direction="row" spacing={1} alignItems="center">
                <Chip label={item.questionType} size="small" color="primary" variant="outlined" />
                {item.answerSetName && (
                    <Chip label={item.answerSetName} size="small" variant="outlined" />
                )}
            </Stack>
        ),
    },
];

const QuestionsPanel: React.FC<QuestionsPanelProps> = ({ onPathChange }) => {
    const [viewMode, setViewMode] = useState<ViewMode>("list");
    const [questions, setQuestions] = useState<Question[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [answerSets, setAnswerSets] = useState<AnswerSet[]>([]);
    const [answerSetCache, setAnswerSetCache] = useState<Record<number, AnswerSet>>({});
    const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
    const [formValues, setFormValues] = useState<QuestionFormState>(initialFormState);
    const [loading, setLoading] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [formError, setFormError] = useState<string | null>(null);

    // Use URL-based list state for pagination, sorting, and search
    const listState = useListState({ defaultSortBy: "name", defaultSortDirection: "asc", paramPrefix: "q" });

    useEffect(() => {
        const path =
            viewMode === "list"
                ? "Questions"
                : selectedQuestion
                    ? "Update Question"
                    : "Create Question";
        onPathChange?.(path);
    }, [viewMode, selectedQuestion, onPathChange]);

    const loadQuestions = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetchQuestionsPaginated({
                page: listState.page,
                size: listState.pageSize,
                sortBy: listState.sortBy,
                sortDirection: listState.sortDirection,
                search: listState.search || undefined,
            });
            setQuestions(response.content ?? []);
            setTotalCount(response.totalElements);
        } catch (err) {
            console.error("Failed to fetch questions", err);
            setError(err instanceof Error ? err.message : "Unable to load questions");
        } finally {
            setLoading(false);
        }
    }, [listState.page, listState.pageSize, listState.sortBy, listState.sortDirection, listState.search]);

    const loadAnswerSets = useCallback(async () => {
        try {
            const sets = await fetchAnswerSets({ active: true });
            setAnswerSets(sets ?? []);
        } catch (err) {
            console.error("Failed to fetch answer sets", err);
        }
    }, []);

    useEffect(() => {
        loadQuestions();
        loadAnswerSets();
    }, [loadQuestions, loadAnswerSets]);

    const resetForm = () => {
        setFormValues(initialFormState);
        setSelectedQuestion(null);
        setFormError(null);
    };

    const handleSelectQuestion = async (question: Question) => {
        setDetailLoading(true);
        setFormError(null);
        try {
            const detailed = await fetchQuestion(question.id);
            setSelectedQuestion(detailed);
            setFormValues({
                name: detailed.name ?? "",
                questionText: detailed.questionText ?? "",
                questionType: detailed.questionType ?? "MULTIPLE_CHOICE",
                required: Boolean(detailed.required),
                answerSetId: detailed.answerSetId ?? null,
                displayOrder: detailed.displayOrder,
                active: detailed.active ?? true,
                selectedAnswerIds: detailed.selectedAnswerIds ?? [],
                inclusionMode: detailed.inclusionMode ?? InclusionMode.EXCLUDE,
            });
            setViewMode("detail");
            if (detailed.answerSetId && detailed.answers) {
                setAnswerSetCache((prev) => ({
                    ...prev,
                    [detailed.answerSetId as number]: {
                        id: detailed.answerSetId as number,
                        name: detailed.answerSetName ?? "",
                        description: "",
                        active: true,
                        answers: detailed.answers ?? [],
                    } as AnswerSet,
                }));
            }
            // If the question references an answer set not in the active list, fetch and add it
            if (detailed.answerSetId && !answerSets.some((set) => set.id === detailed.answerSetId)) {
                try {
                    const inactiveAnswerSet = await fetchAnswerSet(detailed.answerSetId);
                    setAnswerSets((prev) => [...prev, inactiveAnswerSet]);
                } catch (err) {
                    console.warn(`Could not fetch inactive answer set ${detailed.answerSetId}`, err);
                }
            }
        } catch (err) {
            console.error("Failed to load question", err);
            setError(err instanceof Error ? err.message : "Unable to load question");
        } finally {
            setDetailLoading(false);
        }
    };

    const handleCreateNew = () => {
        resetForm();
        setViewMode("detail");
    };

    const handleBackToList = () => {
        setViewMode("list");
        resetForm();
    };

    const handleFieldChange = (field: keyof QuestionFormState, value: unknown) => {
        setFormValues((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleSubmit = async () => {
        if (!formValues.name.trim() || !formValues.questionText.trim()) {
            setFormError("Name and question text are required");
            return;
        }
        setSaving(true);
        setFormError(null);
        try {
            if (selectedQuestion) {
                await updateQuestion(selectedQuestion.id, {
                    name: formValues.name.trim(),
                    questionText: formValues.questionText.trim(),
                    questionType: formValues.questionType,
                    required: formValues.required,
                    answerSetId: formValues.answerSetId,
                    displayOrder: formValues.displayOrder,
                    active: formValues.active,
                    selectedAnswerIds: formValues.selectedAnswerIds,
                    inclusionMode: formValues.inclusionMode,
                });
            } else {
                await createQuestion({
                    name: formValues.name.trim(),
                    questionText: formValues.questionText.trim(),
                    questionType: formValues.questionType,
                    required: formValues.required,
                    answerSetId: formValues.answerSetId,
                    displayOrder: formValues.displayOrder,
                    active: formValues.active,
                    selectedAnswerIds: formValues.selectedAnswerIds,
                    inclusionMode: formValues.inclusionMode,
                });
            }
            await loadQuestions();
            handleBackToList();
        } catch (err) {
            console.error("Failed to save question", err);
            setFormError(err instanceof Error ? err.message : "Unable to save question");
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        const answerSetId = formValues.answerSetId;
        if (!answerSetId || answerSetCache[answerSetId]) {
            return;
        }
        let cancelled = false;
        const load = async () => {
            try {
                const detailed = await fetchAnswerSet(answerSetId);
                if (!cancelled) {
                    setAnswerSetCache((prev) => ({ ...prev, [answerSetId]: detailed }));
                }
            } catch (err) {
                console.error("Failed to load answer set", err);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [formValues.answerSetId, answerSetCache]);

    const selectedAnswerSet = formValues.answerSetId
        ? answerSetCache[formValues.answerSetId] ?? undefined
        : undefined;

    const answerPreview: AnswerValue[] = selectedAnswerSet?.answers ?? [];

    const handleToggleActive = async (question: Question, active: boolean) => {
        if (!question.id) {
            return;
        }
        try {
            await updateQuestion(question.id, {
                name: question.name,
                questionText: question.questionText ?? "",
                questionType: question.questionType,
                required: question.required,
                answerSetId: question.answerSetId,
                displayOrder: question.displayOrder,
                active,
            });
            await loadQuestions();
        } catch (err) {
            console.error("Failed to toggle question active status", err);
            setError("Unable to update question status");
        }
    };

    const handleDeleteQuestion = async (question: Question) => {
        if (!question.id) {
            return;
        }
        const confirmed = window.confirm(`Delete question "${question.name}"?`);
        if (!confirmed) {
            return;
        }
        try {
            await deleteQuestion(question.id);
            await loadQuestions();
        } catch (err) {
            console.error("Failed to delete question", err);
            setError(err instanceof Error ? err.message : "Unable to delete question");
        }
    };

    const renderActions = (question: Question) => (
        <>
            <Tooltip title="Edit">
                <IconButton
                    onClick={(event) => {
                        event.stopPropagation();
                        handleSelectQuestion(question);
                    }}
                    sx={{ color: "#7498A6" }}
                >
                    <EditOutlined fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
                <IconButton
                    onClick={(event) => {
                        event.stopPropagation();
                        handleDeleteQuestion(question);
                    }}
                    sx={{ color: "#7498A6" }}
                >
                    <DeleteOutline fontSize="small" />
                </IconButton>
            </Tooltip>
        </>
    );
    const isAnswerActive = (answer: AnswerValue) => {
        if (formValues.inclusionMode === InclusionMode.INCLUDE) {
            return formValues.selectedAnswerIds.includes(answer.id);
        } else {
            return !formValues.selectedAnswerIds.includes(answer.id);
        }
    };

    const handleAnswerToggle = (answer: AnswerValue) => {
        const isInArray = formValues.selectedAnswerIds.includes(answer.id);
        if (isInArray) {
            setFormValues((prev) => ({ ...prev, selectedAnswerIds: prev.selectedAnswerIds.filter((id) => id !== answer.id) }));
        } else {
            setFormValues((prev) => ({ ...prev, selectedAnswerIds: [...prev.selectedAnswerIds, answer.id] }));
        }
    };

    const handleInclusionModeToggle = () => {
        const selectedAnswerIds = formValues.selectedAnswerIds;
        const answerSetAnswers = selectedAnswerSet?.answers ?? [];
        const newSelectedAnswerIds = answerSetAnswers.filter((answer) => !selectedAnswerIds.includes(answer.id)).map((answer) => answer.id);
        setFormValues((prev) => ({ ...prev, selectedAnswerIds: newSelectedAnswerIds, inclusionMode: formValues.inclusionMode === InclusionMode.INCLUDE ? InclusionMode.EXCLUDE : InclusionMode.INCLUDE }));
    };

    const renderAnswerPreview = () => {
        if (!formValues.answerSetId) {
            return (
                <Typography variant="body2" color="text.secondary">
                    Select an answer set to preview its values.
                </Typography>
            );
        }

        if (!selectedAnswerSet) {
            return (
                <Typography variant="body2" color="text.secondary">
                    Loading answer set values...
                </Typography>
            );
        }

        if (!answerPreview.length) {
            return (
                <Typography variant="body2" color="text.secondary">
                    This answer set has no answers yet.
                </Typography>
            );
        }

        return (
            <Stack spacing={1}>
                {answerPreview.map((answer) => (
                    <Box
                        key={answer.id ?? answer.name}
                        sx={{
                            border: `1px solid ${theme.palette.grey[200]}`,
                            borderRadius: "3px",
                            px: 2,
                            py: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            bgcolor: theme.palette.background.paper,
                        }}
                    >
                        <Typography variant="body2">{answer.name}</Typography>
                        <Switch size="small" checked={isAnswerActive(answer)} onChange={() => handleAnswerToggle(answer)} />
                    </Box>
                ))}
            </Stack>
        );
    };

    const renderListView = () => (
        <RubricManagementList<Question>
            items={questions}
            totalCount={totalCount}
            loading={loading}
            error={error}
            listState={listState}
            columns={questionColumns}
            getItemKey={(item) => item.id}
            onItemClick={handleSelectQuestion}
            onAddClick={handleCreateNew}
            searchPlaceholder="Search questions by name..."
            addButtonLabel="Add Question"
            itemLabel="questions"
            emptyMessage="No questions found."
            renderActions={renderActions}
            onToggleActive={handleToggleActive}
            isItemActive={(item) => Boolean(item.active)}
        />
    );

    const renderDetailView = () => (
        <Box>
            <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                <Button startIcon={<ArrowBack />} onClick={handleBackToList} variant="outlined">
                    Back to Questions
                </Button>
            </Stack>
            {formError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {formError}
                </Alert>
            )}
            {detailLoading ? (
                <Stack alignItems="center" py={4}>
                    <CircularProgress />
                </Stack>
            ) : (
                <Stack spacing={3}>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                        <TextField
                            label="Name"
                            value={formValues.name}
                            onChange={(event) => handleFieldChange("name", event.target.value)}
                            required
                            fullWidth
                        />
                        <TextField
                            label="Question Text"
                            value={formValues.questionText}
                            onChange={(event) => handleFieldChange("questionText", event.target.value)}
                            required
                            fullWidth
                        />
                        <FormControl fullWidth>
                            <InputLabel id="question-type-label">Question Type</InputLabel>
                            <Select
                                labelId="question-type-label"
                                label="Question Type"
                                value={formValues.questionType}
                                onChange={(event) =>
                                    handleFieldChange("questionType", event.target.value as Question["questionType"])
                                }
                            >
                                {questionTypeOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formValues.required}
                                    onChange={(event) => handleFieldChange("required", event.target.checked)}
                                />
                            }
                            label="Required"
                        />
                    </Stack>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="center">
                        <FormControl fullWidth>
                            <InputLabel id="answer-set-select-label">Answer Set</InputLabel>
                            <Select
                                labelId="answer-set-select-label"
                                label="Answer Set"
                                value={formValues.answerSetId !== null ? String(formValues.answerSetId) : ""}
                                onChange={(event) =>
                                    handleFieldChange(
                                        "answerSetId",
                                        event.target.value === "" ? null : Number(event.target.value)
                                    )
                                }
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {answerSets.map((set) => (
                                    <MenuItem
                                        key={set.id}
                                        value={String(set.id)}
                                        disabled={set.active === false}
                                    >
                                        {set.name}
                                        {set.active === false && (
                                            <Chip
                                                label="Inactive"
                                                size="small"
                                                color="warning"
                                                sx={{ ml: 1 }}
                                            />
                                        )}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={formValues.inclusionMode === InclusionMode.EXCLUDE}
                                onChange={handleInclusionModeToggle}
                            />
                        }
                        label={`Automatically include new answers`}
                    />
                    <Divider />
                    <Box>
                        <Typography variant="subtitle1" gutterBottom>
                            Answer Preview
                        </Typography>
                        {renderAnswerPreview()}
                    </Box>
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Button variant="outlined" onClick={handleBackToList} disabled={saving}>
                            Cancel
                        </Button>
                        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
                            {saving ? "Saving..." : selectedQuestion ? "Update" : "Create"}
                        </Button>
                    </Stack>
                </Stack>
            )}
        </Box>
    );

    return viewMode === "list" ? renderListView() : renderDetailView();
};

export default QuestionsPanel;
